import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ChevronDown, Users, Check, Trash2, Clock, CalendarPlus, AlertTriangle, CheckCircle2, HardHat, FileText, Flag, Briefcase, User, Timer, Scale, Columns3 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { useGroupSort } from "@/components/shared/GroupSort";
import { api, formatApiError } from "@/lib/api";
import { formatHours, formatTime, plur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ShiftReviewDialog } from "@/components/shared/ShiftReviewDialog";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { storage } from "@/lib/storage";
import { toast } from "sonner";
import AbsenceApprovals from "@/components/absences/AbsenceApprovals";
import { AbsenceDialog } from "@/components/absences/AbsenceDialog";
import { AttendanceSingleDialog } from "@/components/AttendanceAddDialogs";
import { TabSwitcher } from "@/components/shared/PagePrimitives";
import { ABSENCE_TYPES, toneOf, todayIso, monthStart, monthEnd, addMonths, weekStart, addDays, monthLabel, czShort, dowOf, STATUS_LABEL } from "@/lib/absences";

const fmtN = (v) => (v ? String(Math.round(v * 100) / 100).replace(".", ",") : "");
// 402: součty vpravo ve stejném pořadí jako souhrn pro mzdy v PDF: kontrola → má odpracovat → odpracoval → nepřítomnost;
// svátky, So+Ne a noc jsou v PDF a Excelu
// Zkratky a pořadí jako v PDF a Excelu (402): volno → má odpracovat → nevyplněno → odpracoval, přesčas, rozdíl
// [zkratka, klíč, celý název, vysvětlení] — celý název ukáže bublina po najetí na zkratku (404)
const SUM_COLS = [["D", "D", "Dovolená", "dny schválené dovolené"], ["N", "N", "Nemoc", "dny nemoci"],
  ["L", "L", "Lékař", "dny u lékaře (i půl dne)"], ["NV", "NV", "Náhradní volno", "dny náhradního volna"],
  ["NP", "NP", "Neplacené volno", "dny neplaceného volna"],
  ["Má", "due", "Má odpracovat", "hodiny: pracovní dny bez svátků a volna × délka směny"],
  ["Nevypl.", "missing", "Nevyplněné dny", "pracovní dny bez docházky i nepřítomnosti — pro účetní má být 0"],
  ["Odpr.", "worked", "Odpracováno", "hodiny z docházky"],
  ["Přesč.", "overtime", "Přesčas", "hodiny — jak se počítá, nastavíte v Nastavení › Docházka"],
  ["Rozdíl", "diff", "Rozdíl", "odpracováno − má odpracovat (h)"]];

// 404: hlavička souhrnu — zkratka s tečkovaným podtržením, po najetí (nebo klávesou Tab) hned bublina s celým názvem
export function SumHead({ short, name, hint, testid }) {
  return (
    <th className="px-2 py-2 border-b border-l border-border font-bold text-center whitespace-nowrap bg-secondary/40">
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} data-testid={testid}
            className="cursor-help underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-sm">{short}</span>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={8} data-testid={`${testid}-tip`}
          className="max-w-[240px] rounded-[10px] bg-foreground text-background px-3 py-2 shadow-lg text-left motion-reduce:animate-none">
          <span className="block text-[13px] font-bold">{name}</span>
          <span className="block text-[12px] font-medium opacity-80 leading-snug mt-0.5 whitespace-normal">{hint}</span>
        </TooltipContent>
      </Tooltip>
    </th>
  );
}

// Docházka › Šichtovnice (176): lidé × dny. Klik do prázdného dne = zapsat
// nepřítomnost (i víc lidem), klik na nepřítomnost = detail / schválit / zrušit.
// PC: celý měsíc; mobil: týden (šipky). Nevyřízené žádosti „křičí" nad mřížkou.
// 402: buňka součtu — „—“ u lidí bez fondu (DPP, IČO), Nevyplněno červeně (0 = zelená fajfka), Rozdíl ±, Přesčas akcentem
function SumCell({ t, k, pid, first }) {
  const v = t?.[k];
  const base = `px-2 border-b border-border text-center tabular-nums whitespace-nowrap ${first ? "border-l" : ""}`;
  if (v == null && ["missing", "due", "diff", "overtime"].includes(k)) return <td className={`${base} text-muted-foreground`} title="Bez fondu (DPP, IČO)">—</td>;
  if (k === "missing") return (
    <td className={base} data-testid={`sheet-missing-${pid}`}>
      {v ? <span className="inline-grid place-items-center min-w-[26px] h-6 px-1.5 rounded-full bg-destructive/15 text-destructive font-bold">{v}</span>
        : <Check size={15} strokeWidth={3} className="inline text-success" aria-label="vše vyplněno" />}
    </td>
  );
  if (k === "due") return <td className={`${base} border-l`} title={`${t.days} prac. dní − svátky ${t.holiday_days || 0} − volno ${fmtN(t.days - (t.holiday_days || 0) - t.due_days) || 0} = ${fmtN(t.due_days)} dní × směna`}>{fmtN(v)}</td>;
  if (k === "worked") return <td className={`${base} font-bold`}>{fmtN(v) || "—"}</td>;
  if (k === "diff") return <td className={`${base} ${v < 0 ? "text-destructive" : ""}`} data-testid={`sheet-diff-${pid}`}>{v > 0 ? "+" : ""}{fmtN(v)}</td>;
  if (k === "overtime") return <td className={`${base} font-bold ${v ? "text-brand" : ""}`} data-testid={`sheet-ot-${pid}`}>{fmtN(v)}</td>;
  // 414: neplacené volno, které si napracuje — do NP se nepočítá (fond se nesnižuje), ukáže se vedle jako ↺
  if (k === "NP" && t?.makeup) return <td className={base} data-testid={`sheet-np-${pid}`}>{fmtN(v)}<span className="ml-1 text-muted-foreground font-semibold" title={`${fmtN(t.makeup)} dne neplaceného volna si napracuje — fond se nesnižuje`}>↺{fmtN(t.makeup)}</span></td>;
  return <td className={base}>{fmtN(v)}</td>;
}

// 404: Filtr a Zobrazení šichtovnice (místo tlačítka Lidé) — stejná lišta jako v Záznamech
const CONTRACT = { HPP: "HPP / DPČ", DPP: "DPP", ICO: "IČO" };
const STAV = [{ value: "missing", label: "Nevyplněné dny" }, { value: "overtime", label: "S přesčasem" }, { value: "pending", label: "Čeká na schválení" }];
const pendingOf = (p) => (p.totals?.pending || 0) > 0
  || Object.values(p.cells || {}).some((c) => c.abs?.some((a) => a.status === "pending") || c.shifts?.some((x) => x.pending));
const lastWord = (n) => { const w = String(n || "").trim().split(/\s+/); return w[w.length - 1] || ""; };
const SHEET_GROUPS = {
  pozice: { label: "Pozice", icon: HardHat, key: (p) => p.position || "", none: "Bez pozice" },
  smlouva: { label: "Smlouva", icon: FileText, key: (p) => p.contract || "", name: (k) => CONTRACT[k] || k,
    order: (a, b) => Object.keys(CONTRACT).indexOf(a) - Object.keys(CONTRACT).indexOf(b) },
};
const SHEET_ORDERS = {
  jmeno: { label: "Jméno", icon: User, dirs: ["A → Z", "Z → A"], def: "asc", val: (p) => p.name },
  prijmeni: { label: "Příjmení", icon: User, dirs: ["A → Z", "Z → A"], def: "asc", val: (p) => lastWord(p.name) },
  worked: { label: "Odpracováno", icon: Clock, dirs: ["Nejméně", "Nejvíc"], def: "desc", val: (p) => p.totals?.worked ?? null },
  overtime: { label: "Přesčas", icon: Timer, dirs: ["Nejméně", "Nejvíc"], def: "desc", val: (p) => p.totals?.overtime ?? null },
  diff: { label: "Rozdíl", icon: Scale, dirs: ["Nejvíc chybí", "Nejvíc navíc"], def: "asc", val: (p) => p.totals?.diff ?? null },
};
const SHEET_DEF = { g: "none", gd: 0, k: "jmeno", dir: "asc" };
const byName = (a, b) => String(a.name).localeCompare(String(b.name), "cs");

// Zobrazení › Tabulka: souhrn vpravo a užší víkendy (přepínače)
function SheetOpts({ opts, setOpts, mobile }) {
  const row = (label, hint, on, set, testid) => (
    <label className={`flex items-center gap-3 rounded-[11px] bg-secondary/60 px-3 ${mobile ? "py-3" : "py-2"} cursor-pointer`}>
      <span className="min-w-0 flex-1"><span className="block text-[13.5px] font-semibold">{label}</span>
        <span className="block text-[12px] text-muted-foreground leading-snug">{hint}</span></span>
      <Switch checked={on} onCheckedChange={set} data-testid={testid} className="shrink-0" />
    </label>
  );
  return (
    <div className="space-y-1.5 pt-2" data-testid="sheet-opts">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground px-0.5"><Columns3 size={14} />Tabulka</div>
      {row("Souhrn vpravo", "Má odpracovat, odpracováno, přesčas, rozdíl", opts.sum, (v) => setOpts({ ...opts, sum: v }), "sheet-opt-sum")}
      {row("Užší víkendy", "So a Ne úzce, pracovní dny dostanou víc místa", opts.narrowWe, (v) => setOpts({ ...opts, narrowWe: v }), "sheet-opt-weekend")}
    </div>
  );
}

export default function AbsenceSheet({ reloadKey = 0, onState, workers = [], projects = [], onRecordSaved, startDate }) {
  const mobile = useIsMobile();
  const [anchor, setAnchor] = useState(startDate || todayIso());   // 402: z Záznamů s Obdobím rovnou na ten měsíc
  const [data, setData] = useState(null);
  const [bump, setBump] = useState(0);
  const [editor, setEditor] = useState(null);   // { user_ids, date_from, kind: "absence" | "attendance" }
  const [detail, setDetail] = useState(null);   // { person, date, a?, shifts }
  const [shift, setShift] = useState(null);     // otevřená Kontrola směny (176c)
  // 404: Zobrazení › schovat souhrn vpravo, zúžit víkendy (pamatuje si prohlížeč)
  const [opts, setOptsState] = useState(() => { const [s1, w1] = String(storage.get("pp-sheet-opts", "1|0")).split("|"); return { sum: s1 !== "0", narrowWe: w1 === "1" }; });
  const setOpts = (o) => { setOptsState(o); storage.set("pp-sheet-opts", `${o.sum ? 1 : 0}|${o.narrowWe ? 1 : 0}`); };
  const showSum = !mobile && opts.sum;
  const thin = (d) => opts.narrowWe && d.weekend;
  const { user } = useAuth();
  const canSeeMoney = ["OWNER", "ASSISTANT"].includes(user?.role);
  // Klik do dne: prázdný = zapsat nepřítomnost; jedna směna = rovnou Kontrola
  // směny (příchod, odchod, fotky, report); víc věcí = výběr.
  // Přepínač v okně zápisu: záznam docházky, nebo nepřítomnost (docházka jen do dneška).
  const entryTabs = editor && editor.date_from <= todayIso() ? (
    <TabSwitcher testid="entry-tabs" value={editor.kind} onChange={(v) => setEditor((e) => ({ ...e, kind: v }))} tabs={[
      { value: "attendance", label: "Docházka", testid: "entry-tab-attendance" },
      { value: "absence", label: "Nepřítomnost", testid: "entry-tab-absence" },
    ]} />
  ) : null;
  const openCell = (p, date, cell) => {
    const a = cell?.abs?.[0];
    const shifts = cell?.shifts || [];
    if (shifts.length === 1 && !a) return setShift(shifts[0].id);
    if (shifts.length || a) return setDetail({ person: p, date, a, shifts });
    // Prázdný den (176d): záložky Docházka / Nepřítomnost; do budoucna jen nepřítomnost.
    return setEditor({ user_ids: [p.id], date_from: date, kind: date <= todayIso() ? "attendance" : "absence" });
  };

  const range = useMemo(() => (mobile
    ? { from: weekStart(anchor), to: addDays(weekStart(anchor), 6) }
    : { from: monthStart(anchor), to: monthEnd(anchor) }), [anchor, mobile]);

  const load = useCallback(() => {
    api.get(`/absences/sheet?date_from=${range.from}&date_to=${range.to}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load, reloadKey, bump]);

  const refresh = () => setBump((b) => b + 1);
  const step = (n) => setAnchor((a) => (mobile ? addDays(weekStart(a), 7 * n) : addMonths(a, n)));
  const people = useMemo(() => data?.people || [], [data]);
  // 404: Filtr (Člověk a Zakázka sdílí adresu se Záznamy) a Zobrazení (řazení, skupiny, souhrn, víkendy)
  const fGroups = useMemo(() => {
    const pos = [...new Set(people.map((p) => p.position).filter(Boolean))].sort((a, b) => a.localeCompare(b, "cs"));
    return [
      { key: "clovek", label: "Člověk", icon: Users, noNeg: true, options: people.map((p) => ({ value: p.id, label: p.name })), match: (p, v) => p.id === v },
      { key: "pozice", label: "Pozice", icon: HardHat, options: [...pos.map((x) => ({ value: x, label: x })), { value: "-", label: "Bez pozice" }],
        match: (p, v) => (p.position || "-") === v },
      { key: "smlouva", label: "Smlouva", icon: FileText, options: Object.entries(CONTRACT).map(([value, label]) => ({ value, label })), match: (p, v) => p.contract === v },
      { key: "stav", label: "Stav", icon: Flag, options: STAV,
        match: (p, v) => (v === "missing" ? (p.totals?.missing || 0) > 0 : v === "overtime" ? (p.totals?.overtime || 0) > 0 : pendingOf(p)) },
      { key: "zakazka", label: "Zakázka", icon: Briefcase, noNeg: true, options: projects.map((x) => ({ value: x.id, label: x.name })),
        match: (p, v) => (p.projects || []).includes(v) },
    ];
  }, [people, projects]);
  const F = useListFilters(people, { groups: fGroups, entity: { one: "člověk", few: "lidé", many: "lidí" } });
  const GS = useGroupSort(F.visible || people, { storageKey: "pp-sheet-view", groups: SHEET_GROUPS, orders: SHEET_ORDERS, def: SHEET_DEF, tie: byName,
    extra: { render: (m) => <SheetOpts opts={opts} setOpts={setOpts} mobile={m} />, active: !opts.sum || opts.narrowWe,
      hint: [!opts.sum && "bez souhrnu", opts.narrowWe && "užší víkendy"].filter(Boolean).join(", "), def: "souhrn vpravo, celé víkendy",
      reset: () => setOpts({ sum: true, narrowWe: false }) } });
  const rows = GS.shown;
  const filtered = F.filterCount > 0;
  // co je vidět, to se stáhne (Šichtovnice PDF / Excel / CSV v menu ⋯)
  const ids = filtered ? (F.visible || []).map((p) => p.id).join(",") : "";
  useEffect(() => { onState?.({ date_from: monthStart(anchor), date_to: monthEnd(anchor), user_ids: ids ? ids.split(",") : [] }); }, [anchor, ids]); // eslint-disable-line react-hooks/exhaustive-deps
  // 402: nevyplněné pracovní dny (počet, kolik lidí, první k doplnění) — z lidí ve filtru (i ve sbalených skupinách)
  const missing = useMemo(() => {
    let count = 0, who = 0, checked = 0, next = null, unsent = 0;
    for (const p of GS.sorted) {
      if (p.totals?.missing == null) continue;
      checked += 1;
      if (!p.totals.missing) continue;
      count += p.totals.missing; who += 1;
      unsent += Object.values(p.cells || {}).filter((c) => c.missing && c.unsent).length;   // 420: má výkaz, jen ho neodeslal
      if (!next) { const date = Object.keys(p.cells).sort().find((k) => p.cells[k].missing); if (date) next = { p, date, cell: p.cells[date] }; }
    }
    return { count, who, checked, next, unsent };
  }, [GS.sorted]);
  const today = todayIso();
  const title = mobile ? `${czShort(range.from)}–${czShort(range.to)} ${range.to.slice(0, 4)}` : monthLabel(range.from);

  const decide = async (a, kind) => {
    try {
      if (kind === "approve") await api.post(`/absences/${a.id}/approve`);
      else if (kind === "reject") await api.post(`/absences/${a.id}/reject`, {});
      else await api.delete(`/absences/${a.id}`);
      toast.success(kind === "approve" ? "Schváleno." : kind === "reject" ? "Zamítnuto." : "Nepřítomnost zrušena.");
      setDetail(null); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const showInSheet = (it) => {
    F.clear();
    setAnchor(it.date_from);
    setTimeout(() => document.querySelector(`[data-testid="sheet-row-${it.user_id}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 400);
  };

  const cols = 1 + (data?.days?.length || 0) + (showSum ? SUM_COLS.length : 0);
  const renderRow = (p) => (
                <tr key={p.id} data-testid={`sheet-row-${p.id}`}>
                  <td className="sticky left-0 z-10 bg-card px-3 py-1 border-b border-border font-semibold">
                    <div className="truncate max-w-[96px] md:max-w-[190px]" title={p.name}>{p.name}</div>
                  </td>
                  {data.days.map((d) => {
                    const cell = p.cells[d.date];
                    const a = cell?.abs?.[0];
                    const off = d.weekend || d.holiday;
                    const pending = a?.status === "pending";
                    return (
                      <td key={d.date} className={`${thin(d) ? "p-0 w-[18px] min-w-[18px] max-w-[18px]" : "p-0.5"} border-b border-border text-center ${off ? "bg-secondary/50" : ""}`}>
                        <button type="button" data-testid={`cell-${p.id}-${d.date}`}
                          onClick={() => openCell(p, d.date, cell)}
                          title={cell?.unsent ? `Neodeslaný denní výkaz${cell.unsent === "returned" ? " (vrácený k opravě)" : ""} — hodiny se objeví, až ho odešle. Připomenout jde na Přehledu.` : cell?.missing ? "Nevyplněno — zapište docházku nebo nepřítomnost" : a ? `${a.type_label || a.code}${a.makeup ? " — napracuje si to, fond se nesnižuje" : ""}${a.when ? ` · ${a.when}` : ""}${pending ? " · čeká na schválení" : ""}` : cell?.worked ? `${formatHours(cell.worked)} odpracováno${cell.overtime ? `, přesčas ${formatHours(cell.overtime)}` : ""} — otevřít směnu` : d.holiday || "Zapsat docházku nebo nepřítomnost"}
                          className={`relative w-full h-9 ${thin(d) ? "min-w-0 rounded-[4px] text-[9.5px] tracking-tighter" : "min-w-[28px] rounded-[7px] text-[11.5px]"} font-extrabold tabular-nums grid place-items-center transition-colors
                            ${a ? `${toneOf(a.code)} border ${pending ? "border-dashed border-brand opacity-80" : "border-transparent"}` : cell?.unsent ? "border border-dashed border-amber-500/70 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20" : cell?.missing ? "border border-dashed border-destructive/60 bg-destructive/10 text-destructive hover:bg-destructive/20" : cell?.overtime ? "bg-brand/15 text-brand hover:bg-brand/25" : "hover:bg-secondary text-muted-foreground"}
                            ${d.date === today && !a ? "ring-1 ring-brand/50" : ""}`}>
                          {a?.makeup && <span className="absolute top-0 right-0.5 text-[9px] leading-none font-black" aria-hidden="true">↺</span>}
                          {cell?.unsent && <span className="absolute top-0 right-0.5 text-[8.5px] leading-none font-black" aria-hidden="true" data-testid="cell-unsent">V</span>}
                          {a ? `${a.part === "half" ? "½" : ""}${a.code}` : cell?.worked ? fmtN(cell.worked) : cell?.missing ? "?" : d.holiday ? "S" : ""}
                          {(cell?.shifts || []).some((x) => x.pending) && <span title="Směna čeká na schválení" className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />}
                        </button>
                      </td>
                    );
                  })}
                  {showSum && SUM_COLS.map(([, key], i) => <SumCell key={key} t={p.totals} k={key} pid={p.id} first={i === 0} />)}
                </tr>);

  return (
    <div className="space-y-4" data-testid="absence-sheet">
      <AbsenceApprovals variant="banner" reloadKey={reloadKey + bump} onChanged={refresh} onShow={showInSheet} />

      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex items-center h-11 p-1 bg-card border border-border rounded-full">
          <button type="button" onClick={() => step(-1)} aria-label={mobile ? "Předchozí týden" : "Předchozí měsíc"} data-testid="sheet-prev"
            className="w-9 h-9 rounded-full grid place-items-center hover:bg-secondary"><ChevronLeft size={18} /></button>
          <span className="px-2 min-w-[132px] text-center font-bold text-[14.5px] tabular-nums whitespace-nowrap" data-testid="sheet-title">{title}</span>
          <button type="button" onClick={() => step(1)} aria-label={mobile ? "Další týden" : "Další měsíc"} data-testid="sheet-next"
            className="w-9 h-9 rounded-full grid place-items-center hover:bg-secondary"><ChevronRight size={18} /></button>
        </div>
        {!(today >= range.from && today <= range.to) && (
          <Button variant="outline" onClick={() => setAnchor(todayIso())} className="h-11 rounded-full font-bold">Dnes</Button>
        )}
        <ListToolbar mobile={mobile} filter={F} sort={GS.sortProp} testid="sheet-toolbar" />
        <div className="hidden lg:flex items-center gap-1.5 ml-auto flex-wrap min-h-[44px]" data-testid="sheet-legend">
          {ABSENCE_TYPES.map((t) => <span key={t.code} className={`h-7 px-2.5 rounded-full text-[12px] font-bold inline-flex items-center border ${toneOf(t.code)}`}>{t.code} {t.label}</span>)}
          <span className="h-7 px-2.5 rounded-full text-[12px] font-bold inline-flex items-center border border-dashed border-brand text-brand">čeká</span>
          <span className="h-7 px-2.5 rounded-full text-[12px] font-bold inline-flex items-center border border-border bg-card" title="Neplacené volno, které si napracuje jindy v měsíci — fond se nesnižuje">↺ napracuje</span>
          <span className="h-7 px-2.5 rounded-full text-[12px] font-bold inline-flex items-center border border-dashed border-amber-500/70 bg-amber-500/10 text-amber-700 dark:text-amber-400" title="Denní výkaz rozepsaný, ale neodeslaný — ten den nemá hodiny">?<sup className="text-[8px] ml-px mr-1">V</sup>neodeslaný výkaz</span>
        </div>
      </div>

      {/* 402: kontrola vyplnění — souhrn pro účetní má mít 0 nevyplněných pracovních dnů */}
      {data && missing.count > 0 && (
        <div className="rounded-[16px] border border-destructive/30 bg-destructive/[0.06] px-4 py-3 flex flex-col md:flex-row md:items-center gap-2.5 md:gap-4" data-testid="sheet-missing">
          <AlertTriangle size={20} className="text-destructive shrink-0 hidden md:block" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-sm">
            <b>Nevyplněno {plur(missing.count, "pracovní den", "pracovní dny", "pracovních dní")} u {plur(missing.who, "člověka", "lidí", "lidí")}.</b>{" "}
            <span className="text-muted-foreground">Doplňte docházku nebo nepřítomnost — souhrn pro účetní má mít 0. Den je v tabulce označený <b className="text-destructive">?</b>.</span>
            {missing.unsent > 0 && <span className="block mt-1" data-testid="sheet-unsent">Z toho {plur(missing.unsent, "den má", "dny mají", "dní má")} <b className="text-amber-700 dark:text-amber-400">neodeslaný denní výkaz</b> (oranžové ?) — člověk ho rozepsal, ale neodeslal. Připomenout mu ho jde na Přehledu.</span>}
          </div>
          {missing.next && (
            <Button onClick={() => openCell(missing.next.p, missing.next.date, missing.next.cell)} className="h-11 w-full md:w-auto rounded-full font-bold" data-testid="sheet-missing-next">
              Doplnit {missing.next.p.name.split(" ")[0]} · {czShort(missing.next.date)}
            </Button>
          )}
        </div>
      )}
      {data && missing.count === 0 && missing.checked > 0 && (
        <p className="text-sm font-semibold text-success inline-flex items-center gap-1.5" data-testid="sheet-complete"><CheckCircle2 size={16} aria-hidden="true" />Všechny pracovní dny jsou vyplněné — souhrn je kompletní.</p>
      )}

      {GS.sorted.length > 0 && (
      <div className="bg-card border border-border rounded-[20px] overflow-hidden">
        <div className="overflow-x-auto overscroll-x-contain">
          <table className="w-full text-[13px] border-separate border-spacing-0" data-testid="sheet-grid">
            <TooltipProvider delayDuration={100} skipDelayDuration={300}>
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-card text-left font-bold px-3 py-2 border-b border-border min-w-[104px] md:min-w-[160px]">Jméno</th>
                {(data?.days || []).map((d) => (
                  <th key={d.date} title={d.holiday || (thin(d) ? `${d.day}. · ${d.dow}` : undefined)}
                    className={`py-1.5 border-b border-border font-bold text-center ${thin(d) ? "px-0 w-[18px] min-w-[18px] max-w-[18px] text-[10.5px]" : "px-0.5 min-w-[32px] md:min-w-[30px]"} ${d.weekend || d.holiday ? "bg-secondary/70 text-muted-foreground" : ""} ${d.date === today ? "text-brand" : ""}`}>
                    <div className="tabular-nums leading-tight">{d.day}</div>
                    <div className={`${thin(d) ? "text-[9px] tracking-tighter" : "text-[10.5px]"} font-semibold text-muted-foreground leading-tight`}>{d.dow}</div>
                  </th>
                ))}
                {showSum && SUM_COLS.map(([h, key, name, hint]) => <SumHead key={key} short={h} name={name} hint={hint} testid={`sum-head-${key}`} />)}
              </tr>
            </thead>
            </TooltipProvider>
            <tbody>
              {GS.groups ? GS.groups.map((g) => (
                <Fragment key={`g-${g.key}`}>
                  <tr data-testid={`sheet-group-${g.key || "none"}`}>
                    <td colSpan={cols} className="p-0 border-b border-border bg-secondary/40">
                      <button type="button" onClick={() => GS.toggle(g.key)} aria-expanded={!GS.closed.has(g.key)}
                        className="sticky left-0 inline-flex items-center gap-2 px-3 h-9 text-[13px] font-bold hover:text-brand">
                        <ChevronDown size={15} className={`transition-transform motion-reduce:transition-none ${GS.closed.has(g.key) ? "-rotate-90" : ""}`} />
                        {g.label}
                        <span className="font-semibold text-muted-foreground">· {plur(g.rows.length, "člověk", "lidé", "lidí")} · {formatHours(g.rows.reduce((a, x) => a + (x.totals?.worked || 0), 0))}</span>
                      </button>
                    </td>
                  </tr>
                  {!GS.closed.has(g.key) && g.rows.map(renderRow)}
                </Fragment>
              )) : rows.map(renderRow)}
            </tbody>
          </table>
        </div>
      </div>
      )}
      {data && GS.sorted.length === 0 && (
        <div className="bg-card border border-border rounded-[20px] p-6 text-center text-sm text-muted-foreground" data-testid="sheet-empty">
          {filtered ? "Filtru nikdo neodpovídá." : "Zatím tu nikdo není. Šichtovnice ukazuje zaměstnance a každého, kdo má v tomto období docházku nebo nepřítomnost."}
        </div>
      )}
      <p className="text-[13px] text-muted-foreground">
        Klikněte do prázdného dne a zapište docházku nebo nepřítomnost (i více lidem). Klik na číslo = detail směny s fotkami a reportem. Číslo = odpracované hodiny, <span className="text-brand font-bold">oranžově</span> = den s přesčasem, <b>S</b> = svátek, čárkovaně = čeká na schválení. Souhrn pro mzdy (fond, svátky, víkendy, noc) je v PDF a Excelu.
      </p>

      <AbsenceDialog open={editor?.kind !== "attendance" && !!editor} onClose={() => setEditor(null)} mode="manager" people={people}
        initial={editor || undefined} onSaved={refresh} topSlot={entryTabs} />
      <AttendanceSingleDialog open={editor?.kind === "attendance"} onClose={() => setEditor(null)} workers={workers.filter((w) => w.role !== "DESIGNER")} projects={projects}
        initial={editor ? { user_id: editor.user_ids?.[0], date: editor.date_from } : undefined} topSlot={entryTabs}
        onSaved={() => { refresh(); onRecordSaved?.(); }} />

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent sheet className="md:max-w-md" data-testid="absence-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.person.name}
                  {detail.a && <span className={`inline-flex items-center h-7 px-2.5 rounded-full text-[13px] font-bold border ${toneOf(detail.a.code)}`}>{detail.a.type_label || detail.a.code}</span>}
                </DialogTitle>
                <DialogDescription>{detail.a?.when || `${dowOf(detail.date)} ${czShort(detail.date)}`}</DialogDescription>
              </DialogHeader>
              {detail.a && (
                <div className="space-y-1.5 text-sm">
                  <div><span className="text-muted-foreground">Stav: </span><b>{detail.a.source === "daysheet" ? "zapsáno v denním výkazu" : STATUS_LABEL[detail.a.status]}</b></div>
                  {detail.a.makeup && <div className="rounded-[12px] bg-secondary px-3 py-2" data-testid="detail-makeup"><b>Napracuje si to</b><span className="block text-[13px] text-muted-foreground">Fond se nesnižuje — hodiny musí odpracovat jindy v měsíci. Co je nad fond, je přesčas.</span></div>}
                  {detail.a.part === "time" && detail.a.t_from && <div><span className="text-muted-foreground">Čas: </span><b>{detail.a.t_from}–{detail.a.t_to}</b></div>}
                  {detail.a.hours != null && detail.a.source === "daysheet" && <div><span className="text-muted-foreground">Hodin: </span><b>{fmtN(detail.a.hours)}</b></div>}
                  {detail.a.note && <div><span className="text-muted-foreground">Poznámka: </span>„{detail.a.note}"</div>}
                  {detail.a.created_by_name && <div className="text-muted-foreground">Zapsal(a) {detail.a.created_by_name}</div>}
                </div>
              )}
              {detail.shifts.length > 0 && (
                <div className="space-y-2" data-testid="detail-shifts">
                  <div className="text-sm font-bold">Docházka</div>
                  {detail.shifts.map((x) => (
                    <button key={x.id} type="button" onClick={() => { setDetail(null); setShift(x.id); }} data-testid={`detail-shift-${x.id}`}
                      className="w-full min-h-[48px] rounded-[12px] border border-border px-3 py-2 flex items-center gap-3 text-left hover:border-brand/50 hover:bg-secondary/40">
                      <Clock size={16} className="text-muted-foreground shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold tabular-nums">{formatTime(x.check_in)}–{x.check_out ? formatTime(x.check_out) : "…"} · {formatHours(x.hours)}</span>
                        <span className="block text-[12.5px] text-muted-foreground truncate">{x.project_name}</span>
                      </span>
                      {x.pending && <span className="text-[11px] font-bold uppercase text-amber-600 dark:text-amber-400 shrink-0">ke schválení</span>}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-col md:flex-row md:justify-end gap-2 pt-2">
                {detail.a?.id && <Button variant="outline" onClick={() => decide(detail.a, "delete")} className="h-11 w-full md:w-auto text-destructive" data-testid="absence-delete"><Trash2 size={15} /> Zrušit nepřítomnost</Button>}
                {detail.a?.id && detail.a.status === "pending" && detail.a.type !== "nemoc" && (
                  <Button variant="outline" onClick={() => decide(detail.a, "reject")} className="h-11 w-full md:w-auto font-bold" data-testid="absence-detail-reject">Zamítnout</Button>
                )}
                {detail.a?.id && detail.a.status === "pending" && (
                  <Button onClick={() => decide(detail.a, "approve")} className="h-11 w-full md:w-auto font-bold bg-success hover:bg-success/90 text-white" data-testid="absence-detail-approve">
                    <Check size={15} /> {detail.a.type === "nemoc" ? "Beru na vědomí" : "Schválit"}
                  </Button>
                )}
                {!detail.a && (
                  <Button variant="outline" onClick={() => { const d = detail; setDetail(null); setEditor({ user_ids: [d.person.id], date_from: d.date, kind: "absence" }); }}
                    className="h-11 w-full md:w-auto" data-testid="detail-add-absence"><CalendarPlus size={15} /> Zapsat nepřítomnost</Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ShiftReviewDialog sid={shift} mode="detail" canSeeMoney={canSeeMoney} onClose={() => setShift(null)} onChanged={refresh} />
    </div>
  );
}
